import { motion } from "framer-motion";
import { MuxBackground } from "./MuxBackground";
import { WalletButton } from "./WalletButton";

// Local hero video — plays directly from public/videos/ (no Mux latency/glitches)
const HERO_VIDEO_ID = "/videos/hero.mp4";

export function Hero() {
  return (
    <section className="relative min-h-[100svh] w-full overflow-hidden">
      <MuxBackground id={HERO_VIDEO_ID} darken={0.55} aurora />

      {/* nav */}
      <header className="relative z-10 flex items-center justify-between px-5 md:px-10 pt-6">
        <a href="#top" className="flex items-center gap-3">
          <span className="size-9 rounded-2xl bg-gradient-to-br from-teal-300 via-teal-400 to-leaf shadow-[0_0_24px_rgba(64,176,194,0.55)]" />
          <span className="font-display text-2xl tracking-tight">GAIA</span>
        </a>
        <nav className="hidden md:flex items-center gap-8 font-mono text-[11px] uppercase tracking-[0.25em] text-bone-dim">
          <a href="#ai-mint"   className="hover:text-teal-300 transition-colors">Mint</a>
          <a href="#connect"    className="hover:text-teal-300 transition-colors">AI Agents</a>
          <a href="#mcp"        className="hover:text-teal-300 transition-colors">AI Mint</a>
          <a href="#roadmap"    className="hover:text-teal-300 transition-colors">Roadmap</a>
        </nav>
        <WalletButton />
      </header>

      {/* hero content */}
      <div className="relative z-10 px-5 md:px-10 pt-20 md:pt-28 pb-32 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
          className="max-w-5xl"
        >
          <div className="eyebrow mb-8">
            <span className="dot" />
            mother nature's memecoin · live on Ethereum
          </div>

          <h1 className="font-display font-medium text-[15vw] md:text-[10rem] leading-[0.85] tracking-tight">
            <span className="block">a coin</span>
            <span className="block">
              for the only{" "}
              <span className="font-serif italic text-aurora glow-teal">home</span>
            </span>
            <span className="block">we've got.</span>
          </h1>

          <p className="mt-10 max-w-2xl text-base md:text-lg text-bone-dim leading-relaxed">
            $GAIA is a memecoin themed around Earth — oceans, forests, animals, and us.
            <span className="text-bone"> 21,000 users</span> · 10 mints each · <span className="text-bone">0.001 ETH</span> per slot.
            All proceeds flow into a Uniswap v4 LP owned by the contract — fees harvestable, liquidity locked.
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <a href="#ai-mint" className="btn btn-primary">
              Mine GAIA
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                <path d="M3 8 H13 M9 4 L13 8 L9 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
            <a href="#mcp" className="btn btn-ghost">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                <circle cx="7" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
                <path d="M7 1.5 V3 M7 11 V12.5 M1.5 7 H3 M11 7 H12.5 M2.8 2.8 L3.9 3.9 M10.1 10.1 L11.2 11.2 M2.8 11.2 L3.9 10.1 M10.1 3.9 L11.2 2.8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              AI agent? Mint via MCP
            </a>
          </div>
        </motion.div>

        {/* "Protect Nature" sticker — floating accent in the corner */}
        <motion.img
          src="/images/hydroflask.jpg"
          alt="Protect Nature, Save the Planet"
          initial={{ opacity: 0, rotate: -18, scale: 0.6 }}
          animate={{ opacity: 1, rotate: -8, scale: 1 }}
          transition={{ delay: 0.6, duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
          className="hidden lg:block absolute right-10 top-32 w-44 xl:w-56 select-none pointer-events-none"
          style={{
            filter: "drop-shadow(0 30px 60px rgba(64, 176, 194, 0.35))",
            mixBlendMode: "lighten",
            zIndex: 1,
          }}
        />

        {/* float-y stats footer */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.7 }}
          className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-px overflow-hidden rounded-[var(--radius-card)] border border-white/10 bg-ink-900/40 backdrop-blur-md"
        >
          {[
            ["0.001 ETH", "per mint slot"],
            ["10,000",    "$GAIA per mint"],
            ["10 / addr", "100k cap"],
            ["210,000",   "total slots"],
          ].map(([k, v]) => (
            <div key={v} className="bg-ink-950/30 px-6 py-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone-dim">{v}</div>
              <div className="mt-2 font-display text-xl md:text-2xl">{k}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* fade into next section */}
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-ink-1000 pointer-events-none z-0" />
    </section>
  );
}
